/**
 * Parameters Screen
 *
 * Central configuration and one-click execution screen. Two responsibilities:
 *
 * 1. Parameter editing: renders all calculation parameters (from CALC_SECTIONS
 *    config) in editable tables grouped by analysis type (delinquencies,
 *    dilution, buyer parent, concentration limits). Each change is immediately
 *    dispatched to Redux (SET_CALC_PARAMS) and auto-saved.
 *
 * 2. "Run Analysis" button: triggers `runAll`, the single canonical orchestrator
 *    (shared with agent-triggered scenario re-runs), which sequentially executes
 *    every analysis step in dependency order. Progress and failures are shown in
 *    the shared RunProgressModal.
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { runAll, BASE } from "Redux/data/actions";
import { FIELD_TYPES, CALC_SECTIONS, CALC_SECTIONS_PAYABLES, getCalcSteps } from "Config/calcParamsConfig";
import RunProgressModal from "Components/RunProgressModal";
import TemplateManager from "Components/TemplateManager";

// ------------------------------------------------------------
// ------------------------------------------------------------
const Parameters = () => {
  const dispatch = useDispatch();
  const calcParams = useSelector((state) => state.project.calcParams);
  const { hasMaster, ratingScale, projectType } = useSelector((state) => state.project);
  const isPayables = projectType === "payables";
  const sections = isPayables ? CALC_SECTIONS_PAYABLES : CALC_SECTIONS;
  const steps = getCalcSteps(projectType);

  const [values, setValues] = useState({});
  const [ratingRows, setRatingRows] = useState([]);

  // Rating scale state
  useEffect(() => {
    if (ratingScale) {
      setRatingRows(JSON.parse(JSON.stringify(ratingScale)));
    }
  }, [ratingScale]);

  const saveRatings = (updated) => {
    setRatingRows(updated);
    dispatch({ type: "SET_RATING_SCALE", ratingScale: updated });
  };

  const updateRatingRow = (index, field, value) => {
    const updated = [...ratingRows];
    updated[index] = { ...updated[index], [field]: value };
    saveRatings(updated);
  };

  const addRatingRow = () => {
    saveRatings([...ratingRows, { rating: "", concLimitPct: 0 }]);
  };

  const removeRatingRow = (index) => {
    saveRatings(ratingRows.filter((_, i) => i !== index));
  };

  // Progress modal state
  const [running, setRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [runError, setRunError] = useState(null);

  useEffect(() => {
    const cp = calcParams || {};
    const init = {};
    sections.forEach((section) => {
      init[section.key] = {};
      const saved = cp[section.key] || {};
      section.fields.forEach((f) => {
        init[section.key][f.key] = saved[f.key] !== undefined ? saved[f.key] : f.default;
      });
    });
    setValues(init);
  }, [calcParams]);

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  // Parse the raw input value using the field type's parser (int, float, string)
  // and immediately persist to both local state and Redux.
  const handleChange = (sectionKey, fieldKey, fieldType, rawValue) => {
    const ft = FIELD_TYPES[fieldType] || FIELD_TYPES.string;
    const updatedSection = {
      ...values[sectionKey],
      [fieldKey]: ft.parse(rawValue),
    };
    setValues((prev) => ({ ...prev, [sectionKey]: updatedSection }));
    dispatch({ type: "SET_CALC_PARAMS", section: sectionKey, params: updatedSection });
  };

  const handleCalculateAll = () => {
    setRunning(true);
    setIsDone(false);
    setRunError(null);
    setStepIndex(0);
    setCurrentStep(steps[0].key);

    const onStep = (stepKey, idx) => {
      setCurrentStep(stepKey);
      setStepIndex(idx);
    };
    const onDone = () => {
      setIsDone(true);
      setCurrentStep(null);
      setStepIndex(steps.length);
    };
    const onError = (msg) => setRunError(msg);

    // runAll throws after onError so the modal can show the failure; swallow the
    // rejection here to avoid an unhandled promise error in the console.
    dispatch(runAll(BASE, onStep, onDone, onError)).catch(() => {});
  };

  return (
    <div>
      {running && (
        <RunProgressModal
          steps={steps}
          currentStep={currentStep}
          stepIndex={stepIndex}
          isDone={isDone}
          error={runError}
          onClose={() => setRunning(false)}
        />
      )}

      <div className="box" style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1.25rem",
      }}>
        <button
          className="button is-medium"
          onClick={handleCalculateAll}
          disabled={running}
          style={{
            borderRadius: "var(--ev-radius-lg)",
            background: "linear-gradient(135deg, #430098 0%, #6B3FA0 50%, #79589F 100%)",
            color: "#fff",
            border: "none",
            fontWeight: 800,
            fontSize: "1rem",
            padding: "0.75em 2.5em",
            boxShadow: "0 6px 20px rgba(67, 0, 152, 0.3)",
            letterSpacing: "0.03em",
          }}
        >
          <span className="icon" style={{ fontSize: "1.1rem" }}>
            <i className="fa-duotone fa-rocket" />
          </span>
          <span>Run Analysis</span>
        </button>
      </div>

      <div style={{ display: "flex", gap: "1.5rem", alignItems: "flex-start" }}>
        {/* Left: Calculation Parameters */}
        <div style={{ flex: "3 1 0" }}>
          <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Calculation Parameters</span>
            <TemplateManager
              templateType="calcParams"
              getCurrentData={() => {
                const cp = {};
                sections.forEach((s) => { cp[s.key] = values[s.key]; });
                return cp;
              }}
              label="Calculation Parameters"
            />
          </div>
          {sections.map((section) => (
            <div key={section.key} style={{ marginBottom: "1rem" }}>
              <div style={{
                display: "flex", alignItems: "center", gap: "0.5rem",
                padding: "0.5rem 0", borderBottom: "2px solid var(--ev-border)", marginBottom: "0.5rem",
              }}>
                <i className={section.icon} style={{ color: "var(--ev-purple)", fontSize: "0.9rem" }} />
                <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>{section.title}</span>
              </div>
              <table className="table is-fullwidth">
                <tbody>
                  {section.fields.map((f) => {
                    const ft = FIELD_TYPES[f.type] || FIELD_TYPES.string;
                    const val = values[section.key]?.[f.key];
                    return (
                      <tr key={f.key}>
                        <td style={{ width: "30%", verticalAlign: "middle" }}>
                          <div style={{ fontWeight: 600, fontSize: "0.8rem" }}>
                            {f.label}
                            {f.unit && (
                              <span style={{ color: "var(--ev-text-muted)", fontWeight: 400, marginLeft: "0.3rem" }}>
                                ({f.unit})
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ width: "120px", verticalAlign: "middle" }}>
                          <input
                            className="input is-small"
                            type={ft.type}
                            step={ft.step}
                            value={val !== undefined ? val : ""}
                            onChange={(e) => handleChange(section.key, f.key, f.type, e.target.value)}
                          />
                        </td>
                        <td style={{ verticalAlign: "middle" }}>
                          <span style={{ fontSize: "0.72rem", color: "var(--ev-text-muted)", lineHeight: 1.3 }}>
                            {f.description}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* Right: Rating Scale (receivables only) */}
        {!isPayables && <div style={{ flex: "1 1 280px", minWidth: "280px" }}>
          <div style={{ fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Rating Scale</span>
            <TemplateManager
              templateType="ratingScale"
              getCurrentData={() => ratingRows}
              label="Rating Scale"
            />
          </div>
          <div style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            padding: "0.5rem 0", borderBottom: "2px solid var(--ev-border)", marginBottom: "0.5rem",
          }}>
            <i className="fa-duotone fa-star" style={{ color: "var(--ev-purple)", fontSize: "0.9rem" }} />
            <span style={{ fontWeight: 700, fontSize: "0.85rem" }}>Credit Rating Tiers</span>
          </div>
          <table className="table is-fullwidth">
            <thead>
              <tr>
                <th>Rating</th>
                <th>Limit %</th>
                <th style={{ width: "40px" }}></th>
              </tr>
            </thead>
            <tbody>
              {ratingRows.map((row, index) => (
                <tr key={index}>
                  <td style={{ verticalAlign: "middle" }}>
                    <input
                      className="input is-small"
                      type="text"
                      value={row.rating}
                      onChange={(e) => updateRatingRow(index, "rating", e.target.value)}
                    />
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <input
                      className="input is-small"
                      type="number"
                      step="0.01"
                      value={row.concLimitPct}
                      onChange={(e) => updateRatingRow(index, "concLimitPct", parseFloat(e.target.value) || 0)}
                    />
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <button
                      className="button is-small is-danger is-light"
                      onClick={() => removeRatingRow(index)}
                      style={{ borderRadius: "6px" }}
                    >
                      <span className="icon" style={{ fontSize: "0.7rem" }}>
                        <i className="fa-duotone fa-trash" />
                      </span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="button is-small" onClick={addRatingRow} style={{ borderRadius: "6px", marginBottom: "0.5rem" }}>
            <span className="icon" style={{ fontSize: "0.8rem" }}>
              <i className="fa-duotone fa-plus" />
            </span>
            <span>Add Rating</span>
          </button>
          <div style={{ fontSize: "0.68rem", color: "var(--ev-text-muted)", fontStyle: "italic" }}>
            Last rating is used as default for unrated buyers.
          </div>
        </div>}
      </div>
    </div>
  );
};

export default Parameters;
